import React, { useState, useMemo } from 'react';
import { FileAnalysis } from '../../services/importService';
import { useFx } from '../../hooks/useFx';
import { useBaseCurrency } from '../../hooks/useBaseCurrency';
import {
  Close,
  ShoppingCart,
  ShoppingCartOutlined,
  LocalShipping,
  Inventory2,
  AccountBalance,
  Warning,
  CheckCircle,
  ArrowForward,
  CategoryOutlined,
  Percent,
} from '@mui/icons-material';

interface Props {
  open: boolean;
  onClose: () => void;
  analysis: FileAnalysis;
  batchId: number;
}

type Tab = 'materials' | 'contracts' | 'po' | 'ratio' | 'inventory' | 'gl' | 'mrp';

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: 'materials', label: 'Materials Map', icon: <CategoryOutlined sx={{ fontSize: 16 }} /> },
  { key: 'contracts', label: 'Contracts', icon: <ShoppingCart sx={{ fontSize: 16 }} /> },
  { key: 'po', label: 'Purchase Orders', icon: <LocalShipping sx={{ fontSize: 16 }} /> },
  { key: 'ratio', label: 'Ratio POs', icon: <Percent sx={{ fontSize: 16 }} /> },
  { key: 'inventory', label: 'Inventory', icon: <Inventory2 sx={{ fontSize: 16 }} /> },
  { key: 'gl', label: 'GL Valuation', icon: <AccountBalance sx={{ fontSize: 16 }} /> },
  { key: 'mrp', label: 'MRP / Demand', icon: <ShoppingCartOutlined sx={{ fontSize: 16 }} /> },
];

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

const ImportAnalysisDialog: React.FC<Props> = ({ open, onClose, analysis, batchId }) => {
  const [activeTab, setActiveTab] = useState<Tab>('materials');

  if (!open) return null;

  const c = analysis.contracts;
  const po = analysis.purchaseOrders;
  const inv = analysis.inventory;
  const gl = analysis.gl;

  // Only show tabs that have data
  const visibleTabs = TABS.filter((tab) => {
    if (tab.key === 'materials') return !!analysis.materialsMap;
    if (tab.key === 'contracts') return !!c;
    if (tab.key === 'po') return !!po;
    if (tab.key === 'ratio') return !!analysis.ratioPOs && analysis.ratioPOs.count > 0;
    if (tab.key === 'inventory') return !!inv;
    if (tab.key === 'gl') return !!gl;
    if (tab.key === 'mrp') return !!analysis.mrp || analysis.workerType === 'strauss-mrp';
    return false;
  });

  // Auto-select first visible tab if current tab is hidden
  const effectiveTab = visibleTabs.some(t => t.key === activeTab) ? activeTab : (visibleTabs[0]?.key || 'materials');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-[960px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800">
            Import Analysis — Batch #{batchId}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Close sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Tabs — only those with data */}
        <div className="flex border-b border-gray-200 px-5">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                effectiveTab === tab.key
                  ? 'border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm">
          {effectiveTab === 'materials' && analysis.materialsMap && <MaterialsMapTab data={analysis.materialsMap} />}
          {effectiveTab === 'contracts' && c && <ContractsTab data={c} />}
          {effectiveTab === 'po' && po && <POTab data={po} />}
          {effectiveTab === 'ratio' && analysis.ratioPOs && <RatioPOsTab data={analysis.ratioPOs} />}
          {effectiveTab === 'inventory' && inv && <InventoryTab data={inv} />}
          {effectiveTab === 'gl' && gl && <GLTab data={gl} />}
          {effectiveTab === 'mrp' && analysis.mrp && <MRPTab data={analysis.mrp} />}
          {effectiveTab === 'mrp' && !analysis.mrp && analysis.workerType === 'strauss-mrp' && <StraussMrpTab data={analysis} />}
          {visibleTabs.length === 0 && <NoData />}
        </div>
      </div>
    </div>
  );
};

const NoData = () => (
  <div className="text-gray-400 text-center py-8">No analysis data available for this file.</div>
);

const Stat: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
  <div className="bg-gray-50 rounded-lg px-3 py-2">
    <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
    <div className={`text-lg font-semibold ${color || 'text-gray-800'}`}>{value}</div>
  </div>
);

const FlowArrow = () => <ArrowForward sx={{ fontSize: 14 }} className="text-gray-300 mx-1" />;

/* ──────────── Materials Map Tab ──────────── */
type MaterialMapEntry = { sapCode: string; formulaCode: string; usedIn: string[] };

const MaterialsMapTab: React.FC<{ data: MaterialMapEntry[] }> = ({ data }) => {
  // Group by formulaCode
  const byFormula: Record<string, MaterialMapEntry[]> = {};
  for (const entry of data) {
    if (!byFormula[entry.formulaCode]) byFormula[entry.formulaCode] = [];
    byFormula[entry.formulaCode].push(entry);
  }
  const formulaCodes = Object.keys(byFormula);

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
        <span className="font-medium text-gray-700">{data.length}</span> SAP material codes mapped to{' '}
        <span className="font-medium text-gray-700">{formulaCodes.length}</span> SpotLock formulas
      </div>

      {formulaCodes.map((formulaCode) => {
        const entries = byFormula[formulaCode];
        return (
          <div key={formulaCode} className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="bg-indigo-50 px-3 py-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500" />
              <span className="text-xs font-semibold text-indigo-700">{formulaCode}</span>
              <span className="text-[10px] text-gray-400 ml-auto">{entries.length} SAP codes</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left py-1.5 px-3 font-medium">SAP Code</th>
                  <th className="text-left py-1.5 px-3 font-medium">Used In</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.sapCode} className="border-b border-gray-50">
                    <td className="py-1.5 px-3 font-mono text-gray-700">{e.sapCode}</td>
                    <td className="py-1.5 px-3">
                      <div className="flex gap-1">
                        {e.usedIn.map((u) => (
                          <span
                            key={u}
                            className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              u === 'contracts'
                                ? 'bg-blue-50 text-blue-600 border border-blue-200'
                                : u === 'inventory'
                                ? 'bg-green-50 text-green-600 border border-green-200'
                                : 'bg-gray-50 text-gray-600 border border-gray-200'
                            }`}
                          >
                            {u}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

/* ──────────── Contracts Tab ──────────── */
const ContractsTab: React.FC<{ data: NonNullable<FileAnalysis['contracts']> }> = ({ data }) => (
  <div className="space-y-4">
    {/* Flow summary */}
    <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
      <span className="font-medium text-gray-700">{data.totalRows}</span> rows
      <FlowArrow />
      <span className="font-medium text-gray-700">{data.parsed}</span> parsed
      <FlowArrow />
      <span className="font-medium text-gray-700">{data.uniqueContracts}</span> unique contracts
      {data.deletedCount > 0 && (
        <>
          <span className="mx-2 text-gray-300">|</span>
          <span className="text-red-400">{data.deletedCount} deleted</span>
        </>
      )}
      {data.unknownMaterialCount > 0 && (
        <>
          <span className="mx-2 text-gray-300">|</span>
          <span className="text-orange-400">{data.unknownMaterialCount} unknown material</span>
        </>
      )}
    </div>

    {/* Stats */}
    <div className="grid grid-cols-4 gap-2">
      <Stat label="Total Rows" value={data.totalRows} />
      <Stat label="Parsed" value={data.parsed} />
      <Stat label="Contracts" value={data.uniqueContracts} />
      <Stat label="Skipped" value={data.skipped + data.deletedCount} color={data.skipped > 0 ? 'text-orange-500' : undefined} />
    </div>

    {/* By Formula */}
    {data.byFormula.length > 0 && (
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">By Formula</h4>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-100">
              <th className="text-left py-1.5 font-medium">Formula</th>
              <th className="text-right py-1.5 font-medium">Lines</th>
              <th className="text-right py-1.5 font-medium">Target (MT)</th>
              <th className="text-right py-1.5 font-medium">Open (MT)</th>
            </tr>
          </thead>
          <tbody>
            {data.byFormula.map((f) => (
              <tr key={f.formulaCode} className="border-b border-gray-50">
                <td className="py-1.5 font-medium text-indigo-700">{f.formulaCode}</td>
                <td className="text-right py-1.5">{f.count}</td>
                <td className="text-right py-1.5">{fmt(f.targetQtyMT)}</td>
                <td className="text-right py-1.5">{fmt(f.openQtyMT)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/* ──────────── PO Tab ──────────── */
type POLine = NonNullable<FileAnalysis['purchaseOrders']>['lines'][number];
type FormulaAvg = {
  formulaCode: string; count: number; totalQtyMT: number; remainingMT: number;
  weightedAvgPrice: number; displayCurrency: string; isMixed: boolean;
};

const POTab: React.FC<{ data: NonNullable<FileAnalysis['purchaseOrders']> }> = ({ data }) => {
  const baseCurrency = useBaseCurrency();
  const fx = useFx(baseCurrency);

  // Group lines by formula
  const linesByFormula: Record<string, POLine[]> = {};
  for (const line of data.lines) {
    if (!linesByFormula[line.formulaCode]) linesByFormula[line.formulaCode] = [];
    linesByFormula[line.formulaCode].push(line);
  }

  // Compute per-formula weighted avg with FX conversion
  const formulaAvgs: FormulaAvg[] = useMemo(() => {
    return data.byFormula.map((f) => {
      const lines = linesByFormula[f.formulaCode] || [];
      const currencies = new Set(lines.filter(l => l.netPrice > 0).map(l => l.currency.toUpperCase()));
      const isMixed = currencies.size > 1;
      if (!isMixed && currencies.size === 1) {
        // Single currency — use backend value as-is
        return {
          formulaCode: f.formulaCode, count: f.count,
          totalQtyMT: f.totalQtyMT, remainingMT: f.remainingMT,
          weightedAvgPrice: f.weightedAvgPrice,
          displayCurrency: [...currencies][0],
          isMixed: false,
        };
      }

      // Mixed currencies — convert each line to base currency
      let totalWeighted = 0;
      let totalRem = 0;
      for (const l of lines) {
        if (l.netPrice <= 0 || l.remainingMT <= 0) continue;
        const { amountBase } = fx.convertToBase(l.netPrice, l.currency);
        totalWeighted += (amountBase ?? 0) * l.remainingMT;
        totalRem += l.remainingMT;
      }
      return {
        formulaCode: f.formulaCode, count: f.count,
        totalQtyMT: f.totalQtyMT, remainingMT: f.remainingMT,
        weightedAvgPrice: totalRem > 0 ? Math.round(totalWeighted / totalRem * 100) / 100 : 0,
        displayCurrency: baseCurrency,
        isMixed: true,
      };
    });
  }, [data.byFormula, linesByFormula, fx, baseCurrency]);

  const anyMixed = formulaAvgs.some(f => f.isMixed);


  return (
    <div className="space-y-4">
      {/* Flow summary */}
      <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 flex-wrap">
        <span className="font-medium text-gray-700">{data.totalRows}</span> rows
        {data.deletedCount > 0 && (
          <><FlowArrow /><span className="text-red-400">{data.deletedCount} deleted</span></>
        )}
        {data.closedCount > 0 && (
          <><FlowArrow /><span className="text-gray-400">{data.closedCount} closed</span></>
        )}
        {data.noMaterialCount > 0 && (
          <><FlowArrow /><span className="text-orange-400">{data.noMaterialCount} no material</span></>
        )}
        <FlowArrow />
        <span className="font-medium text-green-600">{data.openCount} matched</span>
        {data.overdueCount > 0 && (
          <><FlowArrow /><span className="text-amber-500">{data.overdueCount} overdue → current month</span></>
        )}
        <FlowArrow />
        <span className="font-medium text-indigo-600">{data.supplyPositions} supply positions</span>
      </div>

      {/* Stats */}
      <div className={`grid gap-2 ${data.contractLookupHits > 0 || data.contractLookupMisses > 0 ? 'grid-cols-5' : 'grid-cols-3'}`}>
        <Stat label="Total Rows" value={data.totalRows} />
        <Stat label="Matched POs" value={data.openCount} color="text-green-600" />
        {data.overdueCount > 0 && <Stat label="Overdue" value={data.overdueCount} color="text-amber-500" />}
        {(data.contractLookupHits > 0 || data.contractLookupMisses > 0) && (
          <Stat label="Contract Matches" value={data.contractLookupHits} />
        )}
        {data.contractLookupMisses > 0 && (
          <Stat label="No Contract" value={data.contractLookupMisses} color="text-orange-500" />
        )}
      </div>

      {/* By Formula summary with weighted avg */}
      {formulaAvgs.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary by Formula</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left py-1.5 font-medium">Formula</th>
                <th className="text-right py-1.5 font-medium">Lines</th>
                <th className="text-right py-1.5 font-medium">Total (MT)</th>
                <th className="text-right py-1.5 font-medium">Remaining (MT)</th>
                <th className="text-right py-1.5 font-medium">Wtd Avg Price/MT</th>
              </tr>
            </thead>
            <tbody>
              {formulaAvgs.map((f) => (
                <tr key={f.formulaCode} className="border-b border-gray-50">
                  <td className="py-1.5 font-medium text-indigo-700">{f.formulaCode}</td>
                  <td className="text-right py-1.5">{f.count}</td>
                  <td className="text-right py-1.5">{fmt(f.totalQtyMT)}</td>
                  <td className="text-right py-1.5">{fmt(f.remainingMT)}</td>
                  <td className="text-right py-1.5 font-semibold">
                    {f.weightedAvgPrice > 0 ? (
                      <>
                        {fmt(f.weightedAvgPrice)}{' '}
                        <span className="text-gray-400 font-normal">{f.displayCurrency}</span>
                        {f.isMixed && <span className="text-orange-400 font-normal ml-0.5" title="Converted from mixed currencies">*</span>}
                      </>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-1 text-[10px] text-gray-400">
            Wtd Avg = Σ(price/MT × remainingMT) / totalRemainingMT
            {anyMixed && (
              <span className="text-orange-400 ml-2">
                * Mixed currencies converted to {baseCurrency} using FX rates{fx.asOf ? ` as of ${fx.asOf.substring(0, 10)}` : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Individual PO lines grouped by formula */}
      {formulaAvgs.map((f) => {
        const lines = linesByFormula[f.formulaCode] || [];
        if (lines.length === 0) return null;
        return (
          <div key={f.formulaCode} className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="bg-indigo-50 px-3 py-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500" />
              <span className="text-xs font-semibold text-indigo-700">{f.formulaCode}</span>
              <span className="text-[10px] text-gray-400 ml-auto">
                {lines.length} lines · {fmt(f.remainingMT)} MT remaining · avg {fmt(f.weightedAvgPrice)} {f.displayCurrency}/MT
                {f.isMixed && <span className="text-orange-400">*</span>}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-100">
                    <th className="text-left py-1 px-2 font-medium">PO</th>
                    <th className="text-left py-1 px-2 font-medium">Supplier</th>
                    <th className="text-left py-1 px-2 font-medium">Contract</th>
                    <th className="text-right py-1 px-2 font-medium">PO Qty</th>
                    <th className="text-right py-1 px-2 font-medium">Supplied</th>
                    <th className="text-right py-1 px-2 font-medium">Remaining</th>
                    <th className="text-right py-1 px-2 font-medium">Price/MT</th>
                    <th className="text-right py-1 px-2 font-medium">Weight</th>
                    <th className="text-left py-1 px-2 font-medium">Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const weight = f.remainingMT > 0 ? (l.remainingMT / f.remainingMT * 100) : 0;
                    return (
                      <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-1 px-2 font-mono">{l.poDoc}/{l.poItem}</td>
                        <td className="py-1 px-2 truncate max-w-[120px]" title={l.supplier}>{l.supplier}</td>
                        <td className="py-1 px-2 font-mono text-gray-500">{l.contractRef || '—'}</td>
                        <td className="text-right py-1 px-2">{fmt(l.poQtyMT)}</td>
                        <td className="text-right py-1 px-2 text-gray-400">{fmt(l.suppliedMT)}</td>
                        <td className="text-right py-1 px-2 font-medium">{fmt(l.remainingMT)}</td>
                        <td className="text-right py-1 px-2">
                          {l.netPrice > 0 ? (
                            <>{fmt(l.netPrice)} <span className="text-gray-400">{l.currency}</span></>
                          ) : (
                            <span className="text-orange-400">—</span>
                          )}
                        </td>
                        <td className="text-right py-1 px-2 text-gray-400">{weight.toFixed(1)}%</td>
                        <td className="py-1 px-2">
                          {l.isOverdue ? (
                            <span className="text-amber-500" title={`Original: ${l.originalDeliveryDate}`}>
                              {l.deliveryDate} <span className="text-[9px]">(was {l.originalDeliveryDate})</span>
                            </span>
                          ) : (
                            <span className="text-gray-500">{l.deliveryDate || '—'}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr className="bg-gray-50 font-medium">
                    <td className="py-1.5 px-2" colSpan={3}>Total</td>
                    <td className="text-right py-1.5 px-2">{fmt(f.totalQtyMT)}</td>
                    <td className="text-right py-1.5 px-2 text-gray-400">
                      {fmt(f.totalQtyMT - f.remainingMT)}
                    </td>
                    <td className="text-right py-1.5 px-2">{fmt(f.remainingMT)}</td>
                    <td className="text-right py-1.5 px-2">
                      {f.weightedAvgPrice > 0 ? (
                        <>{fmt(f.weightedAvgPrice)} <span className="text-gray-400 font-normal">{f.displayCurrency}</span></>
                      ) : '—'}
                    </td>
                    <td className="text-right py-1.5 px-2 text-gray-400">100%</td>
                    <td className="py-1.5 px-2" />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ──────────── Inventory Tab ──────────── */
const InventoryTab: React.FC<{ data: NonNullable<FileAnalysis['inventory']> }> = ({ data }) => {
  // Detect if KG breakdown is available (Strauss has it, Carmit doesn't)
  const hasKgBreakdown = data.byFormula.some(f => f.unrestrictedKG > 0 || f.inTransitKG > 0 || f.qualityCheckKG > 0);
  const matchedRows = data.byFormula.reduce((s, f) => s + f.rowCount, 0);

  return (
    <div className="space-y-4">
      {/* Flow summary */}
      <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
        <span className="font-medium text-gray-700">{data.totalRows}</span> rows
        <FlowArrow />
        <span className="font-medium text-green-600">{matchedRows} matched</span>
        <FlowArrow />
        <span className="font-medium text-gray-700">{data.materialsFound}</span> materials
        {data.supplyPositions != null && (
          <><FlowArrow /><span className="font-medium text-indigo-600">{data.supplyPositions} positions</span></>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Total Rows" value={data.totalRows} />
        <Stat label="Matched Rows" value={matchedRows} color="text-green-600" />
        <Stat label="Materials" value={data.materialsFound} />
      </div>

      {/* By Formula detail */}
      {data.byFormula.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Inventory by Formula</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left py-1.5 font-medium">Formula</th>
                <th className="text-right py-1.5 font-medium">Rows</th>
                {hasKgBreakdown && (
                  <>
                    <th className="text-right py-1.5 font-medium">Unrestricted</th>
                    <th className="text-right py-1.5 font-medium">In Transit</th>
                    <th className="text-right py-1.5 font-medium">QC</th>
                  </>
                )}
                <th className="text-right py-1.5 font-medium">Total (MT)</th>
              </tr>
            </thead>
            <tbody>
              {data.byFormula.map((f) => (
                <tr key={f.formulaCode} className="border-b border-gray-50">
                  <td className="py-1.5">
                    <div className="font-medium text-indigo-700">{f.formulaCode}</div>
                    <div className="text-[10px] text-gray-400">{f.materialCode}</div>
                  </td>
                  <td className="text-right py-1.5">{f.rowCount}</td>
                  {hasKgBreakdown && (
                    <>
                      <td className="text-right py-1.5">{fmt(f.unrestrictedKG)} kg</td>
                      <td className="text-right py-1.5">{fmt(f.inTransitKG)} kg</td>
                      <td className="text-right py-1.5">{fmt(f.qualityCheckKG)} kg</td>
                    </>
                  )}
                  <td className="text-right py-1.5 font-semibold text-gray-800">{fmt(f.totalMT)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ──────────── GL Tab ──────────── */
const GLTab: React.FC<{ data: NonNullable<FileAnalysis['gl']> }> = ({ data }) => (
  <div className="space-y-4">
    {data.skipped ? (
      <div className="flex items-center gap-2 text-orange-600 bg-orange-50 rounded-lg px-4 py-3 text-xs">
        <Warning sx={{ fontSize: 16 }} />
        {data.provided
          ? 'GL file was provided but contained no usable material-level entries. Inventory valuation was skipped.'
          : 'GL file was not provided. Inventory valuation was skipped.'}
      </div>
    ) : (
      <>
        {/* Flow summary */}
        <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          <span className="font-medium text-gray-700">{data.totalRows}</span> total rows
          <FlowArrow />
          <span className="font-medium text-green-600">{data.validEntries}</span> valid entries
          <FlowArrow />
          <CheckCircle sx={{ fontSize: 12 }} className="text-green-500" />
          <span className="text-green-600">valuation calculated</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Total Rows" value={data.totalRows} />
          <Stat label="Valid Entries" value={data.validEntries} />
          <Stat label="Skipped" value={data.totalRows - data.validEntries} />
        </div>
      </>
    )}

    {/* Valuations table */}
    {data.valuations.length > 0 && (
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Weighted Average Valuation
        </h4>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-100">
              <th className="text-left py-1.5 font-medium">Formula</th>
              <th className="text-right py-1.5 font-medium">Inventory (MT)</th>
              <th className="text-right py-1.5 font-medium">Covered (MT)</th>
              <th className="text-right py-1.5 font-medium">GL Entries</th>
              <th className="text-right py-1.5 font-medium">Avg Price/MT</th>
            </tr>
          </thead>
          <tbody>
            {data.valuations.map((v) => (
              <tr key={v.formulaCode} className="border-b border-gray-50">
                <td className="py-1.5 font-medium text-indigo-700">{v.formulaCode}</td>
                <td className="text-right py-1.5">{fmt(v.inventoryMT)}</td>
                <td className="text-right py-1.5">
                  <span className={v.coveredMT < v.inventoryMT ? 'text-orange-500' : 'text-green-600'}>
                    {fmt(v.coveredMT)}
                  </span>
                  {v.coveredMT < v.inventoryMT && (
                    <span className="ml-1 text-[10px] text-orange-400">
                      ({Math.round(v.coveredMT / v.inventoryMT * 100)}%)
                    </span>
                  )}
                </td>
                <td className="text-right py-1.5">{v.glEntriesUsed}</td>
                <td className="text-right py-1.5 font-semibold text-gray-800">
                  {fmt(v.avgPricePerMT)} <span className="text-gray-400 font-normal">{v.currency}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Local currency breakdown */}
        <div className="mt-3 text-[10px] text-gray-400">
          {data.valuations.map((v) => (
            <div key={v.formulaCode}>
              {v.formulaCode}: {fmt(v.avgPriceLocalPerMT)} {v.localCurrency}/MT (local)
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

/* ──────────── Ratio POs Tab ──────────── */
const RatioPOsTab: React.FC<{ data: NonNullable<FileAnalysis['ratioPOs']> }> = ({ data }) => {
  if (data.count === 0) {
    return <div className="text-gray-400 text-center py-8">No ratio POs detected.</div>;
  }

  // Group by formula
  const byFormula: Record<string, typeof data.lines> = {};
  for (const l of data.lines) {
    if (!byFormula[l.formulaCode]) byFormula[l.formulaCode] = [];
    byFormula[l.formulaCode].push(l);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 text-xs text-gray-500 bg-amber-50 rounded-lg px-3 py-2 flex-wrap">
        <span className="font-medium text-amber-700">{data.count}</span> ratio PO(s)
        <span className="mx-2 text-gray-300">|</span>
        <span className="font-medium text-amber-600">{fmt(data.totalQtyMT)} MT</span> total quantity
        <span className="mx-2 text-gray-300">|</span>
        <span className="text-gray-400">No fixed price — excluded from value calculations</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Ratio POs" value={data.count} color="text-amber-600" />
        <Stat label="Total Qty (MT)" value={fmt(data.totalQtyMT)} color="text-amber-600" />
        <Stat label="Formulas" value={Object.keys(byFormula).length} />
      </div>

      {Object.entries(byFormula).map(([formulaCode, fLines]) => {
        const totalMT = fLines.reduce((s, l) => s + l.openQtyMT, 0);
        return (
          <div key={formulaCode} className="border border-amber-100 rounded-lg overflow-hidden">
            <div className="bg-amber-50 px-3 py-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-xs font-semibold text-amber-700">{formulaCode}</span>
              <span className="text-[10px] text-gray-400 ml-auto">
                {fLines.length} lines · {fmt(totalMT)} MT
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-100">
                    <th className="text-left py-1 px-2 font-medium">Contract</th>
                    <th className="text-left py-1 px-2 font-medium">Supplier</th>
                    <th className="text-right py-1 px-2 font-medium">Open Qty (MT)</th>
                    <th className="text-right py-1 px-2 font-medium">Ratio</th>
                    <th className="text-left py-1 px-2 font-medium">Delivery</th>
                    <th className="text-left py-1 px-2 font-medium">UOM</th>
                  </tr>
                </thead>
                <tbody>
                  {fLines.map((l, idx) => (
                    <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-1 px-2 font-mono">{l.contractId}/{l.contractItem}</td>
                      <td className="py-1 px-2 truncate max-w-[150px]" title={l.supplier}>{l.supplier}</td>
                      <td className="text-right py-1 px-2 font-medium">{fmt(l.openQtyMT)}</td>
                      <td className="text-right py-1 px-2 text-amber-600 font-medium">{l.ratio}</td>
                      <td className="py-1 px-2 text-gray-500">{l.deliveryDate || '—'}</td>
                      <td className="py-1 px-2 text-gray-400">{l.uom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ──────────── MRP / Demand Tab ──────────── */
const MRPTab: React.FC<{ data: NonNullable<FileAnalysis['mrp']> }> = ({ data }) => (
  <div className="space-y-4">
    {/* Flow summary */}
    <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 flex-wrap">
      <span className="font-medium text-gray-700">{data.totalRows}</span> rows
      <FlowArrow />
      <span className="font-medium text-green-600">{data.nipukRows}</span> ניפוק rows
      {data.skippedRows > 0 && (
        <>
          <span className="mx-2 text-gray-300">|</span>
          <span className="text-orange-400">{data.skippedRows} skipped</span>
        </>
      )}
      <FlowArrow />
      <span className="font-medium text-gray-700">{data.aggregatedPeriods}</span> material×month periods
      <FlowArrow />
      <span className="font-medium text-indigo-600">{data.demandPositions} demand positions</span>
    </div>

    {/* Stats */}
    <div className="grid grid-cols-4 gap-2">
      <Stat label="Total Rows" value={data.totalRows} />
      <Stat label="ניפוק Rows" value={data.nipukRows} color="text-green-600" />
      <Stat label="Periods" value={data.aggregatedPeriods} />
      <Stat label="Demand Positions" value={data.demandPositions} color="text-indigo-600" />
    </div>

    {/* By Formula summary */}
    {data.byFormula.length > 0 && (
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary by Formula</h4>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-100">
              <th className="text-left py-1.5 font-medium">Formula</th>
              <th className="text-right py-1.5 font-medium">Months</th>
              <th className="text-right py-1.5 font-medium">Total (MT)</th>
              <th className="text-right py-1.5 font-medium">ניפוק Rows</th>
            </tr>
          </thead>
          <tbody>
            {data.byFormula.map((f) => (
              <tr key={f.formulaCode} className="border-b border-gray-50">
                <td className="py-1.5 font-medium text-indigo-700">{f.formulaCode}</td>
                <td className="text-right py-1.5">{f.months}</td>
                <td className="text-right py-1.5 font-semibold">{fmt(f.totalMT)}</td>
                <td className="text-right py-1.5 text-gray-500">{f.nipukRowsAggregated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Monthly breakdown */}
    {data.periods.length > 0 && (
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Monthly Breakdown</h4>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-100">
              <th className="text-left py-1.5 font-medium">Formula</th>
              <th className="text-left py-1.5 font-medium">Month</th>
              <th className="text-right py-1.5 font-medium">Quantity (MT)</th>
              <th className="text-right py-1.5 font-medium">Rows Aggregated</th>
            </tr>
          </thead>
          <tbody>
            {data.periods.map((p, idx) => (
              <tr key={idx} className="border-b border-gray-50">
                <td className="py-1.5 font-medium text-indigo-700">{p.formulaCode}</td>
                <td className="py-1.5 text-gray-600">{p.monthKey}</td>
                <td className="text-right py-1.5 font-medium">{fmt(p.totalMT)}</td>
                <td className="text-right py-1.5 text-gray-500">{p.rowCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/** Strauss MRP: one row per material, one column per month (strauss-mrp worker metadata). */
const StraussMrpTab: React.FC<{ data: FileAnalysis }> = ({ data }) => {
  const months = data.monthsInFile ?? [];
  const skipped = data.pastMonthsSkipped ?? [];
  const imported = months.filter((m) => !skipped.includes(m));
  const byFormula = data.byFormula ?? [];
  const range = (list: string[]) => (list.length ? `${list[0]} – ${list[list.length - 1]}` : '—');
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 flex-wrap">
        <span className="font-medium text-gray-700">{months.length}</span> months in file ({range(months)})
        <FlowArrow />
        <span className="font-medium text-indigo-600">{imported.length}</span> imported ({range(imported)})
        {skipped.length > 0 && (
          <>
            <span className="mx-2 text-gray-300">|</span>
            <span className="text-orange-500">{skipped.length} past months skipped ({range(skipped)}) — history</span>
          </>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Stat label="Materials" value={data.materialsFound ?? byFormula.length} />
        <Stat label="Months in file" value={months.length} />
        <Stat label="Months imported" value={imported.length} color="text-indigo-600" />
        <Stat label="Unmapped materials" value={data.unmappedMaterials?.length ?? 0}
          color={(data.unmappedMaterials?.length ?? 0) > 0 ? 'text-red-600' : undefined} />
      </div>

      {(data.unmappedMaterials?.length ?? 0) > 0 && (
        <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
          Unmapped (not imported): {data.unmappedMaterials!.join(', ')}
        </div>
      )}

      {byFormula.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary by Formula</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left py-1.5 font-medium">Formula</th>
                <th className="text-right py-1.5 font-medium">Months</th>
                <th className="text-right py-1.5 font-medium">Total (MT)</th>
                <th className="text-right py-1.5 font-medium">Avg / month (MT)</th>
              </tr>
            </thead>
            <tbody>
              {byFormula.map((f) => (
                <tr key={f.formulaCode} className="border-b border-gray-50">
                  <td className="py-1.5 font-medium text-indigo-700">{f.formulaCode}</td>
                  <td className="text-right py-1.5">{f.monthsCount}</td>
                  <td className="text-right py-1.5 font-semibold">{fmt(f.totalMT)}</td>
                  <td className="text-right py-1.5 text-gray-500">{f.monthsCount ? fmt(f.totalMT / f.monthsCount) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-gray-400 mt-2">
            Month-by-month changes vs. the active forecast: “Compare to Current” → Changed positions.
            The current month is prorated to the remaining days when used in the tables.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImportAnalysisDialog;
